import React from "react";

/**
 * Hand-crafted SVG illustrations of actual industrial MRO products.
 * Each SVG draws the real product geometry (bearings, filters, hard hats, etc.)
 * so the catalog reads as a real industrial supplier portfolio, not generic icons.
 *
 * Routing priority (most specific → least): mfg_pn match → brand+category match → category.
 */

/* ------------------------- shared chrome / textures ------------------------- */
const Backdrop = ({ from = "#0F172A", to = "#020617", accent = "#7DD3FC", grain = true }) => (
  <>
    <defs>
      <linearGradient id="bgGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={from} />
        <stop offset="100%" stopColor={to} />
      </linearGradient>
      <radialGradient id="bgGlow" cx="0.8" cy="0.1" r="0.65">
        <stop offset="0%" stopColor={accent} stopOpacity="0.18" />
        <stop offset="100%" stopColor={accent} stopOpacity="0" />
      </radialGradient>
      <pattern id="grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.6" />
      </pattern>
    </defs>
    <rect width="320" height="240" fill="url(#bgGrad)" />
    {grain && <rect width="320" height="240" fill="url(#grid)" />}
    <rect width="320" height="240" fill="url(#bgGlow)" />
  </>
);

const Shadow = ({ cx = 160, cy = 210, rx = 90, ry = 8 }) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="rgba(0,0,0,0.5)" opacity="0.55" />
);

/* --------------------------- product illustrations --------------------------- */

/* SKF / NTN deep-groove ball bearing — concentric rings with balls */
const Bearing = ({ accent = "#7DD3FC", brand = "SKF" }) => (
  <>
    <Shadow rx={78} />
    <defs>
      <radialGradient id="metal" cx="0.35" cy="0.3" r="0.85">
        <stop offset="0%" stopColor="#E5E7EB" />
        <stop offset="55%" stopColor="#9CA3AF" />
        <stop offset="100%" stopColor="#374151" />
      </radialGradient>
      <radialGradient id="seal" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor={accent} stopOpacity="0.85" />
        <stop offset="100%" stopColor="#0B1220" />
      </radialGradient>
    </defs>
    {/* Outer race */}
    <circle cx="160" cy="115" r="78" fill="url(#metal)" />
    <circle cx="160" cy="115" r="78" fill="none" stroke="#0B1220" strokeWidth="2" />
    {/* Seal (rubber face) */}
    <circle cx="160" cy="115" r="62" fill="url(#seal)" />
    {/* 9 balls visible through cutaway */}
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const r = 50;
      const cx = 160 + Math.cos(a) * r;
      const cy = 115 + Math.sin(a) * r;
      return (
        <circle key={i} cx={cx} cy={cy} r="6.5" fill="url(#metal)" stroke="#0B1220" strokeWidth="0.8" />
      );
    })}
    {/* Inner race */}
    <circle cx="160" cy="115" r="34" fill="url(#metal)" />
    <circle cx="160" cy="115" r="34" fill="none" stroke="#0B1220" strokeWidth="2" />
    {/* Bore */}
    <circle cx="160" cy="115" r="18" fill="#020617" />
    <circle cx="160" cy="115" r="18" fill="none" stroke="#9CA3AF" strokeWidth="1" />
    {/* Brand etching on seal */}
    <text x="160" y="118" textAnchor="middle" fontSize="10" fontWeight="700" fill={accent} opacity="0.9" letterSpacing="2">
      {brand}
    </text>
  </>
);

/* Donaldson heavy-duty air filter — pleated cylinder */
const AirFilter = ({ accent = "#FCD34D", brand = "Donaldson" }) => (
  <>
    <Shadow rx={68} ry={9} />
    <defs>
      <linearGradient id="filterBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FDE68A" />
        <stop offset="50%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#92400E" />
      </linearGradient>
    </defs>
    {/* Top end cap */}
    <ellipse cx="160" cy="55" rx="62" ry="10" fill="#1F2937" stroke="#0B1220" strokeWidth="1.5" />
    {/* Body with pleats */}
    <rect x="98" y="55" width="124" height="120" fill="url(#filterBody)" />
    {Array.from({ length: 16 }).map((_, i) => (
      <line
        key={i}
        x1={100 + i * 7.6}
        y1="58"
        x2={100 + i * 7.6}
        y2="173"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="1.2"
      />
    ))}
    {/* Bottom end cap */}
    <ellipse cx="160" cy="175" rx="62" ry="10" fill="#1F2937" stroke="#0B1220" strokeWidth="1.5" />
    {/* Brand band */}
    <rect x="98" y="110" width="124" height="22" fill="#0B1220" opacity="0.85" />
    <text x="160" y="125" textAnchor="middle" fontSize="11" fontWeight="800" fill={accent} letterSpacing="2.5">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Spin-on oil filter — Fleetguard / Donaldson lube can */
const SpinOnFilter = ({ accent = "#FBBF24", brand = "Fleetguard" }) => (
  <>
    <Shadow rx={50} ry={7} />
    <defs>
      <linearGradient id="canBody" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#92400E" />
        <stop offset="50%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#7C2D12" />
      </linearGradient>
    </defs>
    {/* Top mounting plate */}
    <rect x="118" y="48" width="84" height="14" rx="2" fill="#1F2937" stroke="#0B1220" />
    <circle cx="160" cy="55" r="6" fill="#0B1220" />
    <circle cx="160" cy="55" r="3" fill={accent} opacity="0.6" />
    {/* Can body */}
    <rect x="106" y="62" width="108" height="120" rx="6" fill="url(#canBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Crimp ribs */}
    <rect x="106" y="70" width="108" height="3" fill="rgba(0,0,0,0.35)" />
    <rect x="106" y="170" width="108" height="3" fill="rgba(0,0,0,0.35)" />
    {/* Label */}
    <rect x="116" y="92" width="88" height="56" fill="#0B1220" opacity="0.92" />
    <text x="160" y="113" textAnchor="middle" fontSize="12" fontWeight="800" fill={accent} letterSpacing="1.5">
      {brand.toUpperCase()}
    </text>
    <text x="160" y="132" textAnchor="middle" fontSize="9" fontWeight="600" fill="#E5E7EB" letterSpacing="1.5">
      LUBE FILTER
    </text>
  </>
);

/* 3M N95 1860 respirator — dome with straps */
const N95Respirator = ({ accent = "#FB923C", brand = "3M" }) => (
  <>
    <Shadow rx={72} ry={8} />
    <defs>
      <radialGradient id="maskBody" cx="0.5" cy="0.4" r="0.7">
        <stop offset="0%" stopColor="#FAFAFA" />
        <stop offset="100%" stopColor="#9CA3AF" />
      </radialGradient>
    </defs>
    {/* Mask shape */}
    <path
      d="M 88 95 Q 160 60 232 95 Q 232 160 200 180 Q 160 200 120 180 Q 88 160 88 95 Z"
      fill="url(#maskBody)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Nose foam */}
    <ellipse cx="160" cy="100" rx="36" ry="8" fill="#E5E7EB" stroke="#0B1220" />
    {/* Embossed seams */}
    <path d="M 100 130 Q 160 110 220 130" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
    <path d="M 110 155 Q 160 140 210 155" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
    {/* Straps */}
    <path d="M 92 95 Q 60 75 38 60" fill="none" stroke="#1F2937" strokeWidth="3" />
    <path d="M 228 95 Q 260 75 282 60" fill="none" stroke="#1F2937" strokeWidth="3" />
    <path d="M 92 165 Q 56 175 38 200" fill="none" stroke="#1F2937" strokeWidth="3" />
    <path d="M 228 165 Q 264 175 282 200" fill="none" stroke="#1F2937" strokeWidth="3" />
    {/* Brand stamp */}
    <text x="160" y="170" textAnchor="middle" fontSize="14" fontWeight="900" fill="#0B1220" letterSpacing="2">
      {brand}
    </text>
    <rect x="142" y="180" width="36" height="9" fill={accent} />
    <text x="160" y="187.5" textAnchor="middle" fontSize="8" fontWeight="800" fill="#0B1220" letterSpacing="1.5">
      N95
    </text>
  </>
);

/* MSA V-Gard / hard hat — Type 1 full-brim */
const HardHat = ({ accent = "#FBBF24", brand = "MSA" }) => (
  <>
    <Shadow rx={88} ry={9} />
    <defs>
      <linearGradient id="hatBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FAFAFA" />
        <stop offset="100%" stopColor="#9CA3AF" />
      </linearGradient>
    </defs>
    {/* Brim */}
    <ellipse cx="160" cy="155" rx="92" ry="14" fill="url(#hatBody)" stroke="#0B1220" strokeWidth="1.5" />
    {/* Dome */}
    <path
      d="M 80 155 Q 80 70 160 65 Q 240 70 240 155 Z"
      fill="url(#hatBody)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Ridges */}
    <path d="M 105 145 Q 160 100 215 145" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" />
    <path d="M 130 70 L 130 152" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
    <path d="M 190 70 L 190 152" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
    {/* Front badge */}
    <rect x="138" y="105" width="44" height="18" rx="2" fill="#0B1220" />
    <text x="160" y="118" textAnchor="middle" fontSize="11" fontWeight="800" fill={accent} letterSpacing="2">
      {brand}
    </text>
  </>
);

/* Safety goggles — Honeywell */
const Goggles = ({ accent = "#7DD3FC", brand = "Honeywell" }) => (
  <>
    <Shadow rx={92} ry={7} />
    <defs>
      <linearGradient id="lensTint" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={accent} stopOpacity="0.7" />
        <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.95" />
      </linearGradient>
    </defs>
    {/* Frame */}
    <path
      d="M 60 100 Q 90 80 130 90 L 130 140 Q 90 150 60 130 Z"
      fill="#1F2937"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    <path
      d="M 260 100 Q 230 80 190 90 L 190 140 Q 230 150 260 130 Z"
      fill="#1F2937"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Lenses */}
    <path d="M 68 102 Q 90 88 124 96 L 124 134 Q 90 142 68 128 Z" fill="url(#lensTint)" />
    <path d="M 252 102 Q 230 88 196 96 L 196 134 Q 230 142 252 128 Z" fill="url(#lensTint)" />
    {/* Bridge */}
    <rect x="128" y="108" width="64" height="14" fill="#1F2937" stroke="#0B1220" />
    {/* Strap */}
    <path d="M 60 115 Q 30 130 30 150" fill="none" stroke="#1F2937" strokeWidth="4" />
    <path d="M 260 115 Q 290 130 290 150" fill="none" stroke="#1F2937" strokeWidth="4" />
    <text x="160" y="180" textAnchor="middle" fontSize="11" fontWeight="700" fill={accent} letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Cordless impact driver / drill — DeWalt-style or Bosch-style */
const PowerDrill = ({ accent = "#FBBF24", brand = "DeWalt" }) => (
  <>
    <Shadow rx={92} ry={8} />
    <defs>
      <linearGradient id="toolBody" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={accent} />
        <stop offset="60%" stopColor="#92400E" />
        <stop offset="100%" stopColor="#1C1917" />
      </linearGradient>
    </defs>
    {/* Body */}
    <path
      d="M 60 90 L 200 90 Q 220 90 220 105 L 220 140 Q 220 155 200 155 L 140 155 L 130 200 L 95 200 L 105 155 L 90 155 L 90 125 L 60 110 Z"
      fill="url(#toolBody)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Chuck */}
    <rect x="200" y="100" width="40" height="40" rx="4" fill="#1F2937" stroke="#0B1220" />
    <circle cx="220" cy="120" r="10" fill="#0B1220" stroke="#9CA3AF" strokeWidth="1.2" />
    <circle cx="220" cy="120" r="4" fill="#9CA3AF" />
    {/* Drill bit */}
    <path d="M 240 117 L 290 110 L 290 130 L 240 123 Z" fill="#9CA3AF" stroke="#0B1220" />
    {/* Trigger */}
    <path d="M 110 150 L 122 175 L 105 175 Z" fill="#0B1220" />
    {/* Battery */}
    <rect x="80" y="195" width="60" height="20" rx="3" fill="#1F2937" stroke="#0B1220" />
    <rect x="90" y="200" width="40" height="4" rx="1" fill={accent} opacity="0.7" />
    {/* Brand stamp */}
    <text x="140" y="125" textAnchor="middle" fontSize="14" fontWeight="900" fill="#0B1220" letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Philips LED T8 tube — long fluorescent-style tube */
const LedTube = ({ accent = "#FCD34D", brand = "Philips" }) => (
  <>
    <Shadow rx={120} ry={5} />
    <defs>
      <linearGradient id="tubeBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FAFAFA" />
        <stop offset="50%" stopColor={accent} stopOpacity="0.85" />
        <stop offset="100%" stopColor="#D1D5DB" />
      </linearGradient>
    </defs>
    {/* End caps */}
    <rect x="30" y="105" width="14" height="40" rx="2" fill="#1F2937" stroke="#0B1220" />
    <rect x="276" y="105" width="14" height="40" rx="2" fill="#1F2937" stroke="#0B1220" />
    {/* Pins */}
    <rect x="24" y="115" width="6" height="3" fill="#D1D5DB" />
    <rect x="24" y="132" width="6" height="3" fill="#D1D5DB" />
    <rect x="290" y="115" width="6" height="3" fill="#D1D5DB" />
    <rect x="290" y="132" width="6" height="3" fill="#D1D5DB" />
    {/* Tube */}
    <rect x="44" y="110" width="232" height="30" rx="15" fill="url(#tubeBody)" stroke="#0B1220" strokeWidth="1.5" />
    <rect x="44" y="115" width="232" height="6" rx="3" fill="#FFFFFF" opacity="0.55" />
    {/* Brand */}
    <text x="160" y="129" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0B1220" letterSpacing="3">
      {brand.toUpperCase()} · LED T8
    </text>
  </>
);

/* HID / metal halide lamp — Osram bulb shape */
const HidLamp = ({ accent = "#FBBF24", brand = "Osram" }) => (
  <>
    <Shadow rx={50} ry={7} />
    <defs>
      <radialGradient id="glassGrad" cx="0.5" cy="0.4" r="0.6">
        <stop offset="0%" stopColor="#FEF3C7" />
        <stop offset="100%" stopColor="#92400E" />
      </radialGradient>
    </defs>
    {/* Bulb */}
    <ellipse cx="160" cy="100" rx="50" ry="58" fill="url(#glassGrad)" stroke="#0B1220" strokeWidth="1.5" />
    {/* Arc tube inside */}
    <rect x="148" y="80" width="24" height="40" rx="3" fill="#1F2937" opacity="0.55" />
    <line x1="160" y1="80" x2="160" y2="120" stroke={accent} strokeWidth="2" opacity="0.85" />
    {/* Neck */}
    <rect x="142" y="155" width="36" height="14" fill="#9CA3AF" stroke="#0B1220" />
    {/* Base E40 */}
    <path
      d="M 138 169 L 138 200 Q 138 208 146 208 L 174 208 Q 182 208 182 200 L 182 169 Z"
      fill="#6B7280"
      stroke="#0B1220"
    />
    <line x1="138" y1="180" x2="182" y2="180" stroke="#0B1220" />
    <line x1="138" y1="190" x2="182" y2="190" stroke="#0B1220" />
    <line x1="138" y1="200" x2="182" y2="200" stroke="#0B1220" />
    {/* Brand */}
    <text x="160" y="105" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0B1220" letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Loctite threadlocker bottle — squat industrial bottle */
const Bottle = ({ accent = "#3B82F6", brand = "Loctite", colour = "#1E40AF", label = "243" }) => (
  <>
    <Shadow rx={48} ry={6} />
    <defs>
      <linearGradient id="bottleGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={accent} />
        <stop offset="100%" stopColor={colour} />
      </linearGradient>
    </defs>
    {/* Cap */}
    <rect x="140" y="50" width="40" height="22" rx="3" fill="#0B1220" stroke="#0B1220" />
    {/* Nozzle */}
    <rect x="152" y="40" width="16" height="14" rx="2" fill="#1F2937" />
    {/* Bottle body */}
    <path
      d="M 110 75 Q 110 70 115 70 L 205 70 Q 210 70 210 75 L 210 195 Q 210 205 200 205 L 120 205 Q 110 205 110 195 Z"
      fill="url(#bottleGrad)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* White label */}
    <rect x="120" y="100" width="80" height="80" fill="#FAFAFA" stroke="#0B1220" />
    <text x="160" y="125" textAnchor="middle" fontSize="12" fontWeight="900" fill={colour} letterSpacing="1.5">
      {brand.toUpperCase()}
    </text>
    <text x="160" y="158" textAnchor="middle" fontSize="22" fontWeight="900" fill={colour} letterSpacing="1">
      {label}
    </text>
    <rect x="120" y="167" width="80" height="3" fill={colour} />
  </>
);

/* 3M VHB Tape roll — cylindrical roll on side */
const TapeRoll = ({ accent = "#FB923C", brand = "3M" }) => (
  <>
    <Shadow rx={80} ry={9} />
    <defs>
      <radialGradient id="rollGrad" cx="0.35" cy="0.5" r="0.65">
        <stop offset="0%" stopColor="#E5E7EB" />
        <stop offset="60%" stopColor="#9CA3AF" />
        <stop offset="100%" stopColor="#374151" />
      </radialGradient>
    </defs>
    {/* Outer disc */}
    <ellipse cx="160" cy="120" rx="75" ry="70" fill={accent} stroke="#0B1220" strokeWidth="1.5" />
    {/* Tape edge */}
    {Array.from({ length: 8 }).map((_, i) => (
      <ellipse
        key={i}
        cx="160"
        cy="120"
        rx={75 - i}
        ry={70 - i * 0.5}
        fill="none"
        stroke="rgba(0,0,0,0.18)"
        strokeWidth="0.6"
      />
    ))}
    {/* Core */}
    <ellipse cx="160" cy="120" rx="28" ry="26" fill="url(#rollGrad)" stroke="#0B1220" />
    <ellipse cx="160" cy="120" rx="10" ry="9" fill="#0B1220" />
    {/* Brand */}
    <text x="160" y="208" textAnchor="middle" fontSize="13" fontWeight="900" fill={accent} letterSpacing="2">
      {brand} VHB
    </text>
  </>
);

/* Centrifugal pump — KSB / Grundfos volute */
const Pump = ({ accent = "#67E8F9", brand = "Grundfos" }) => (
  <>
    <Shadow rx={92} ry={9} />
    <defs>
      <linearGradient id="pumpBody" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#9CA3AF" />
        <stop offset="100%" stopColor="#1F2937" />
      </linearGradient>
    </defs>
    {/* Motor */}
    <rect x="50" y="85" width="100" height="60" rx="4" fill="url(#pumpBody)" stroke="#0B1220" strokeWidth="1.5" />
    {Array.from({ length: 10 }).map((_, i) => (
      <line key={i} x1={56 + i * 9} y1="90" x2={56 + i * 9} y2="140" stroke="rgba(0,0,0,0.4)" strokeWidth="1" />
    ))}
    {/* Junction box */}
    <rect x="80" y="65" width="40" height="22" rx="2" fill="#1F2937" stroke="#0B1220" />
    {/* Volute (pump housing) */}
    <circle cx="200" cy="115" r="55" fill="url(#pumpBody)" stroke="#0B1220" strokeWidth="1.5" />
    <circle cx="200" cy="115" r="36" fill="#0B1220" />
    <circle cx="200" cy="115" r="20" fill={accent} opacity="0.6" />
    {/* Suction flange (down) */}
    <rect x="186" y="170" width="28" height="20" fill="#1F2937" stroke="#0B1220" />
    <rect x="180" y="188" width="40" height="6" fill="#0B1220" />
    {/* Discharge (up) */}
    <rect x="186" y="42" width="28" height="22" fill="#1F2937" stroke="#0B1220" />
    <rect x="180" y="38" width="40" height="6" fill="#0B1220" />
    {/* Brand */}
    <text x="100" y="118" textAnchor="middle" fontSize="12" fontWeight="900" fill="#FAFAFA" letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Prysmian power cable — coiled cable cross-section + spool */
const CableCoil = ({ accent = "#FCD34D", brand = "Prysmian" }) => (
  <>
    <Shadow rx={88} ry={8} />
    {/* Spool flanges */}
    <ellipse cx="100" cy="120" rx="22" ry="58" fill="#1F2937" stroke="#0B1220" />
    <ellipse cx="220" cy="120" rx="22" ry="58" fill="#1F2937" stroke="#0B1220" />
    {/* Cable wraps */}
    {Array.from({ length: 18 }).map((_, i) => (
      <rect
        key={i}
        x={102 + i * 6.5}
        y="78"
        width="6.5"
        height="84"
        fill={i % 2 === 0 ? "#1E40AF" : "#0F172A"}
        stroke="#0B1220"
        strokeWidth="0.4"
      />
    ))}
    {/* Highlight stripe */}
    <rect x="102" y="118" width="118" height="4" fill={accent} opacity="0.65" />
    {/* Cable end protruding */}
    <path d="M 220 120 L 270 132 L 270 138 L 220 130 Z" fill="#1E40AF" stroke="#0B1220" />
    <circle cx="272" cy="135" r="6" fill={accent} stroke="#0B1220" />
    {/* Brand */}
    <text x="160" y="200" textAnchor="middle" fontSize="12" fontWeight="800" fill={accent} letterSpacing="2.5">
      {brand.toUpperCase()}
    </text>
  </>
);

/* ABB MCB — DIN-rail circuit breaker */
const MCB = ({ accent = "#FB923C", brand = "ABB" }) => (
  <>
    <Shadow rx={70} ry={6} />
    <defs>
      <linearGradient id="mcbBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FAFAFA" />
        <stop offset="100%" stopColor="#9CA3AF" />
      </linearGradient>
    </defs>
    {/* 3 poles side by side */}
    {[0, 1, 2].map((i) => (
      <g key={i} transform={`translate(${108 + i * 36},58)`}>
        <rect x="0" y="0" width="32" height="124" rx="3" fill="url(#mcbBody)" stroke="#0B1220" strokeWidth="1.2" />
        {/* Terminal slots */}
        <rect x="4" y="8" width="24" height="10" fill="#0B1220" />
        <rect x="4" y="106" width="24" height="10" fill="#0B1220" />
        {/* Toggle */}
        <rect x="10" y="48" width="12" height="22" rx="2" fill="#1F2937" stroke="#0B1220" />
        <circle cx="16" cy="59" r="2" fill={accent} />
        {/* Curve label */}
        <text x="16" y="86" textAnchor="middle" fontSize="9" fontWeight="800" fill="#0B1220">C16</text>
      </g>
    ))}
    {/* DIN rail underneath */}
    <rect x="80" y="184" width="160" height="10" fill="#6B7280" stroke="#0B1220" />
    <rect x="80" y="184" width="160" height="3" fill="#9CA3AF" />
    {/* Brand */}
    <text x="160" y="42" textAnchor="middle" fontSize="13" fontWeight="900" fill={accent} letterSpacing="3">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Schneider push button — green flush */
const PushButton = ({ accent = "#22C55E", brand = "Schneider" }) => (
  <>
    <Shadow rx={48} ry={6} />
    <defs>
      <radialGradient id="buttonGrad" cx="0.4" cy="0.35" r="0.7">
        <stop offset="0%" stopColor="#86EFAC" />
        <stop offset="80%" stopColor={accent} />
        <stop offset="100%" stopColor="#14532D" />
      </radialGradient>
    </defs>
    {/* Bezel */}
    <circle cx="160" cy="120" r="64" fill="#1F2937" stroke="#0B1220" strokeWidth="1.5" />
    <circle cx="160" cy="120" r="58" fill="#374151" />
    {/* Mounting nut */}
    {Array.from({ length: 6 }).map((_, i) => {
      const a = (i / 6) * Math.PI * 2;
      const x1 = 160 + Math.cos(a) * 62;
      const y1 = 120 + Math.sin(a) * 62;
      const x2 = 160 + Math.cos(a) * 56;
      const y2 = 120 + Math.sin(a) * 56;
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0B1220" strokeWidth="3" />;
    })}
    {/* Button cap */}
    <circle cx="160" cy="120" r="44" fill="url(#buttonGrad)" stroke="#0B1220" strokeWidth="1.5" />
    <circle cx="148" cy="108" r="10" fill="#FFFFFF" opacity="0.4" />
    {/* Brand */}
    <text x="160" y="205" textAnchor="middle" fontSize="11" fontWeight="800" fill={accent} letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Pneumatic / hydraulic cylinder — Festo / Parker */
const Cylinder = ({ accent = "#7DD3FC", brand = "Festo" }) => (
  <>
    <Shadow rx={110} ry={8} />
    <defs>
      <linearGradient id="cylBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#E5E7EB" />
        <stop offset="50%" stopColor="#9CA3AF" />
        <stop offset="100%" stopColor="#374151" />
      </linearGradient>
    </defs>
    {/* Rear cap */}
    <rect x="20" y="95" width="22" height="50" rx="2" fill="#1F2937" stroke="#0B1220" />
    <circle cx="20" cy="120" r="8" fill="#1F2937" stroke="#0B1220" />
    {/* Tie rods */}
    <line x1="42" y1="100" x2="220" y2="100" stroke="#0B1220" strokeWidth="2" />
    <line x1="42" y1="140" x2="220" y2="140" stroke="#0B1220" strokeWidth="2" />
    {/* Tube body */}
    <rect x="42" y="98" width="180" height="44" rx="4" fill="url(#cylBody)" stroke="#0B1220" strokeWidth="1.5" />
    {/* Port */}
    <rect x="60" y="86" width="12" height="14" fill="#1F2937" />
    <rect x="190" y="86" width="12" height="14" fill="#1F2937" />
    {/* Front cap */}
    <rect x="220" y="95" width="20" height="50" rx="2" fill="#1F2937" stroke="#0B1220" />
    {/* Piston rod */}
    <rect x="240" y="115" width="70" height="10" fill="url(#cylBody)" stroke="#0B1220" />
    <circle cx="306" cy="120" r="8" fill="#1F2937" stroke="#0B1220" />
    {/* Brand */}
    <text x="132" y="125" textAnchor="middle" fontSize="14" fontWeight="900" fill={accent} letterSpacing="3">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Fluke digital multimeter */
const Multimeter = ({ accent = "#FACC15", brand = "Fluke" }) => (
  <>
    <Shadow rx={75} ry={8} />
    {/* Body */}
    <rect x="90" y="50" width="140" height="160" rx="14" fill={accent} stroke="#0B1220" strokeWidth="1.5" />
    {/* Display */}
    <rect x="104" y="64" width="112" height="46" rx="3" fill="#1F2937" stroke="#0B1220" />
    <text x="200" y="100" textAnchor="end" fontSize="22" fontWeight="800" fill="#FAFAFA" fontFamily="monospace">
      117.0
    </text>
    <text x="112" y="78" fontSize="8" fontWeight="700" fill="#9CA3AF" letterSpacing="1">AC V</text>
    {/* Rotary dial */}
    <circle cx="160" cy="148" r="32" fill="#1F2937" stroke="#0B1220" strokeWidth="1.5" />
    <circle cx="160" cy="148" r="22" fill="#374151" />
    <line x1="160" y1="148" x2="160" y2="126" stroke={accent} strokeWidth="3" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
      const x1 = 160 + Math.cos(a) * 28;
      const y1 = 148 + Math.sin(a) * 28;
      const x2 = 160 + Math.cos(a) * 32;
      const y2 = 148 + Math.sin(a) * 32;
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0B1220" strokeWidth="1.2" />;
    })}
    {/* Probe sockets */}
    <circle cx="115" cy="195" r="5" fill="#0B1220" />
    <circle cx="205" cy="195" r="5" fill="#DC2626" stroke="#0B1220" />
    {/* Brand */}
    <text x="160" y="42" textAnchor="middle" fontSize="13" fontWeight="900" fill={accent} letterSpacing="3">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Mitutoyo digital caliper */
const Caliper = ({ accent = "#FACC15", brand = "Mitutoyo" }) => (
  <>
    <Shadow rx={130} ry={5} />
    <defs>
      <linearGradient id="caliperBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#E5E7EB" />
        <stop offset="100%" stopColor="#6B7280" />
      </linearGradient>
    </defs>
    {/* Beam */}
    <rect x="30" y="120" width="260" height="18" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Scale ticks */}
    {Array.from({ length: 24 }).map((_, i) => (
      <line key={i} x1={36 + i * 11} y1="122" x2={36 + i * 11} y2="132" stroke="#0B1220" strokeWidth="0.8" />
    ))}
    {/* Fixed jaw left */}
    <path d="M 30 120 L 30 88 L 56 88 L 56 120 Z" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Fixed lower jaw */}
    <path d="M 30 138 L 30 170 L 56 170 L 56 138 Z" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Sliding jaw */}
    <rect x="170" y="88" width="60" height="80" rx="2" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    <rect x="172" y="100" width="56" height="36" rx="2" fill="#1F2937" />
    <text x="200" y="124" textAnchor="middle" fontSize="13" fontWeight="900" fill={accent} fontFamily="monospace">
      0.000
    </text>
    {/* Upper jaw extension */}
    <path d="M 170 88 L 192 88 L 192 70 L 170 70 Z" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Lower jaw extension */}
    <path d="M 170 168 L 192 168 L 192 200 L 170 200 Z" fill="url(#caliperBody)" stroke="#0B1220" strokeWidth="1.2" />
    {/* Brand */}
    <text x="100" y="160" textAnchor="middle" fontSize="9" fontWeight="800" fill="#0B1220" letterSpacing="2">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Marker pen pack — Staedtler */
const MarkerPen = ({ accent = "#FBBF24", brand = "Staedtler" }) => (
  <>
    <Shadow rx={90} ry={6} />
    {[0, 1, 2].map((i) => (
      <g key={i} transform={`translate(${82 + i * 52},60)`}>
        {/* Cap */}
        <rect x="0" y="0" width="36" height="40" rx="3" fill="#0B1220" />
        <rect x="6" y="6" width="24" height="6" fill={i === 0 ? "#DC2626" : i === 1 ? "#1E40AF" : "#16A34A"} />
        {/* Barrel */}
        <rect x="0" y="40" width="36" height="120" rx="3" fill={i === 0 ? "#DC2626" : i === 1 ? "#1E40AF" : "#16A34A"} stroke="#0B1220" />
        {/* Brand band */}
        <rect x="0" y="90" width="36" height="14" fill="#FAFAFA" />
        <text x="18" y="100" textAnchor="middle" fontSize="6" fontWeight="800" fill="#0B1220" letterSpacing="0.5">
          {brand.toUpperCase()}
        </text>
        {/* Tip */}
        <path d="M 0 160 L 36 160 L 22 180 L 14 180 Z" fill="#1F2937" />
      </g>
    ))}
  </>
);

/* Welding helmet — Lincoln Electric auto-darkening */
const WeldingHelmet = ({ accent = "#FCA5A5", brand = "Lincoln" }) => (
  <>
    <Shadow rx={88} ry={9} />
    <defs>
      <linearGradient id="helmShell" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1F2937" />
        <stop offset="100%" stopColor="#0B1220" />
      </linearGradient>
    </defs>
    {/* Shell */}
    <path
      d="M 70 55 L 250 55 Q 256 55 256 65 L 256 180 Q 256 200 230 200 L 90 200 Q 64 200 64 180 L 64 65 Q 64 55 70 55 Z"
      fill="url(#helmShell)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Top crest */}
    <path d="M 80 65 Q 160 50 240 65" fill="none" stroke={accent} strokeWidth="3" />
    {/* Auto-darkening cartridge */}
    <rect x="105" y="95" width="110" height="48" rx="3" fill="#0B1220" stroke="#374151" strokeWidth="1.5" />
    <rect x="115" y="103" width="90" height="32" rx="2" fill={accent} opacity="0.2" />
    <text x="160" y="123" textAnchor="middle" fontSize="9" fontWeight="800" fill={accent} letterSpacing="2">
      AUTO 9-13
    </text>
    {/* Lower chin guard */}
    <path d="M 90 200 Q 160 215 230 200" fill="#1F2937" stroke="#0B1220" />
    {/* Brand */}
    <text x="160" y="175" textAnchor="middle" fontSize="12" fontWeight="900" fill={accent} letterSpacing="2.5">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Welding electrodes — ESAB stick electrodes in a row */
const WeldingElectrodes = ({ accent = "#FCA5A5", brand = "ESAB" }) => (
  <>
    <Shadow rx={100} ry={6} />
    {Array.from({ length: 7 }).map((_, i) => (
      <g key={i} transform={`translate(${70 + i * 26},60)`}>
        <rect x="0" y="0" width="16" height="150" fill="#A3A3A3" stroke="#0B1220" />
        <rect x="2" y="2" width="12" height="140" fill="#525252" />
        <rect x="0" y="135" width="16" height="15" fill="#1C1917" />
        <rect x="2" y="138" width="12" height="9" fill="#D6D3D1" />
      </g>
    ))}
    {/* Brand */}
    <text x="160" y="200" textAnchor="middle" fontSize="13" fontWeight="900" fill={accent} letterSpacing="3">
      {brand.toUpperCase()} OK 48
    </text>
  </>
);

/* Castrol / Mobil 20L oil drum */
const OilDrum = ({ accent = "#FCD34D", brand = "Castrol", main = "#16A34A" }) => (
  <>
    <Shadow rx={62} ry={7} />
    <defs>
      <linearGradient id="drumBody" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={main} />
        <stop offset="50%" stopColor="#FAFAFA" />
        <stop offset="100%" stopColor={main} />
      </linearGradient>
    </defs>
    {/* Cap & handle */}
    <rect x="140" y="38" width="40" height="12" fill="#1F2937" stroke="#0B1220" />
    <path d="M 120 38 Q 160 25 200 38" fill="none" stroke="#1F2937" strokeWidth="3" />
    {/* Drum body */}
    <path
      d="M 96 55 Q 96 50 102 50 L 218 50 Q 224 50 224 55 L 218 195 Q 218 205 208 205 L 112 205 Q 102 205 102 195 Z"
      fill="url(#drumBody)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Front face label */}
    <rect x="112" y="95" width="96" height="80" fill="#FAFAFA" stroke="#0B1220" strokeWidth="1.2" />
    <rect x="112" y="95" width="96" height="20" fill={main} />
    <text x="160" y="110" textAnchor="middle" fontSize="11" fontWeight="900" fill="#FAFAFA" letterSpacing="2.5">
      {brand.toUpperCase()}
    </text>
    <text x="160" y="142" textAnchor="middle" fontSize="20" fontWeight="900" fill={main} letterSpacing="1.5">
      AWS 46
    </text>
    <text x="160" y="162" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0B1220" letterSpacing="2">
      HYDRAULIC · 20L
    </text>
  </>
);

/* Lockout padlock — Brady red safety lockout */
const Padlock = ({ accent = "#FCA5A5", brand = "Brady" }) => (
  <>
    <Shadow rx={50} ry={6} />
    <defs>
      <linearGradient id="lockBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EF4444" />
        <stop offset="100%" stopColor="#7F1D1D" />
      </linearGradient>
    </defs>
    {/* Shackle */}
    <path
      d="M 130 90 Q 130 45 160 45 Q 190 45 190 90"
      fill="none"
      stroke="#9CA3AF"
      strokeWidth="14"
      strokeLinecap="round"
    />
    <path
      d="M 130 90 Q 130 50 160 50 Q 190 50 190 90"
      fill="none"
      stroke="#E5E7EB"
      strokeWidth="6"
      strokeLinecap="round"
    />
    {/* Body */}
    <rect x="105" y="90" width="110" height="110" rx="10" fill="url(#lockBody)" stroke="#0B1220" strokeWidth="1.5" />
    {/* Keyhole */}
    <circle cx="160" cy="135" r="11" fill="#0B1220" />
    <rect x="156" y="135" width="8" height="20" fill="#0B1220" />
    {/* Danger label */}
    <rect x="115" y="165" width="90" height="22" fill="#FAFAFA" stroke="#0B1220" />
    <text x="160" y="180" textAnchor="middle" fontSize="10" fontWeight="900" fill="#7F1D1D" letterSpacing="2.5">
      DANGER · LOCKOUT
    </text>
    {/* Brand */}
    <text x="160" y="215" textAnchor="middle" fontSize="11" fontWeight="800" fill={accent} letterSpacing="3">
      {brand.toUpperCase()}
    </text>
  </>
);

/* Cleaning spray bottle / concentrate jug — Diversey */
const SprayBottle = ({ accent = "#7DD3FC", brand = "Diversey" }) => (
  <>
    <Shadow rx={56} ry={7} />
    <defs>
      <linearGradient id="sprayGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={accent} stopOpacity="0.6" />
        <stop offset="100%" stopColor="#0EA5E9" />
      </linearGradient>
    </defs>
    {/* Trigger */}
    <path d="M 200 75 L 240 75 L 240 100 L 220 100 L 220 110 L 200 110 Z" fill="#1F2937" stroke="#0B1220" />
    {/* Neck */}
    <rect x="150" y="62" width="50" height="20" fill="#1F2937" stroke="#0B1220" />
    {/* Bottle body */}
    <path
      d="M 100 82 Q 100 75 110 75 L 190 75 Q 200 75 200 82 L 200 200 Q 200 210 190 210 L 110 210 Q 100 210 100 200 Z"
      fill="url(#sprayGrad)"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Label */}
    <rect x="110" y="115" width="80" height="65" fill="#FAFAFA" stroke="#0B1220" />
    <rect x="110" y="115" width="80" height="14" fill="#0EA5E9" />
    <text x="150" y="125" textAnchor="middle" fontSize="9" fontWeight="900" fill="#FAFAFA" letterSpacing="2">
      {brand.toUpperCase()}
    </text>
    <text x="150" y="152" textAnchor="middle" fontSize="18" fontWeight="900" fill="#0EA5E9">
      R1
    </text>
    <text x="150" y="170" textAnchor="middle" fontSize="7" fontWeight="700" fill="#0B1220" letterSpacing="1.5">
      BATHROOM · 5L
    </text>
  </>
);

/* Paper ream — HP A4 */
const PaperReam = ({ accent = "#7DD3FC", brand = "HP" }) => (
  <>
    <Shadow rx={92} ry={6} />
    {/* Ream wrapper */}
    <rect x="60" y="90" width="200" height="110" rx="3" fill="#FAFAFA" stroke="#0B1220" strokeWidth="1.5" />
    {/* Top edge of paper stack */}
    <rect x="60" y="90" width="200" height="10" fill="#E5E7EB" />
    {/* Brand band */}
    <rect x="60" y="115" width="200" height="38" fill={accent} />
    <text x="160" y="142" textAnchor="middle" fontSize="22" fontWeight="900" fill="#0B1220" letterSpacing="3">
      {brand.toUpperCase()}
    </text>
    <text x="160" y="172" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0B1220" letterSpacing="3">
      A4 · 80 GSM · 500 SH
    </text>
    {/* Specks */}
    <line x1="60" y1="160" x2="260" y2="160" stroke="rgba(0,0,0,0.12)" />
    <line x1="60" y1="175" x2="260" y2="175" stroke="rgba(0,0,0,0.12)" />
  </>
);

/* Wipers box — Kimberly-Clark WypAll */
const WipersBox = ({ accent = "#3B82F6", brand = "WypAll" }) => (
  <>
    <Shadow rx={92} ry={7} />
    {/* Box body */}
    <rect x="62" y="78" width="196" height="124" rx="3" fill="#FAFAFA" stroke="#0B1220" strokeWidth="1.5" />
    {/* Top opening */}
    <rect x="100" y="78" width="120" height="20" fill="#1F2937" />
    <path d="M 110 95 Q 160 80 210 95" fill="none" stroke="#FAFAFA" strokeWidth="4" />
    {/* Brand band */}
    <rect x="62" y="110" width="196" height="40" fill={accent} />
    <text x="160" y="138" textAnchor="middle" fontSize="20" fontWeight="900" fill="#FAFAFA" letterSpacing="3">
      {brand.toUpperCase()}
    </text>
    <text x="160" y="175" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0B1220" letterSpacing="3">
      INDUSTRIAL WIPERS X80
    </text>
    <text x="160" y="193" textAnchor="middle" fontSize="9" fontWeight="700" fill="#6B7280" letterSpacing="2">
      160 SHEETS · HEAVY DUTY
    </text>
  </>
);

/* Belt sander — Makita */
const BeltSander = ({ accent = "#22D3EE", brand = "Makita" }) => (
  <>
    <Shadow rx={108} ry={8} />
    {/* Front roller cover */}
    <rect x="50" y="125" width="40" height="40" rx="4" fill="#06B6D4" stroke="#0B1220" />
    {/* Back roller cover */}
    <rect x="230" y="125" width="40" height="40" rx="4" fill="#06B6D4" stroke="#0B1220" />
    {/* Belt */}
    <rect x="60" y="148" width="220" height="14" fill="#1C1917" stroke="#0B1220" />
    <rect x="62" y="150" width="216" height="2" fill="#525252" />
    {/* Body */}
    <path
      d="M 80 70 L 250 70 Q 270 70 270 90 L 270 130 Q 270 145 250 145 L 80 145 Q 60 145 60 130 L 60 90 Q 60 70 80 70 Z"
      fill="#06B6D4"
      stroke="#0B1220"
      strokeWidth="1.5"
    />
    {/* Handle */}
    <path d="M 100 65 L 195 65 Q 205 65 205 55 L 205 40 Q 205 30 195 30 L 100 30 Q 90 30 90 40 L 90 55 Q 90 65 100 65 Z"
      fill="#1F2937" stroke="#0B1220" strokeWidth="1.5" />
    {/* Dust port */}
    <rect x="265" y="85" width="40" height="22" fill="#1F2937" stroke="#0B1220" />
    {/* Brand */}
    <text x="155" y="115" textAnchor="middle" fontSize="14" fontWeight="900" fill="#0B1220" letterSpacing="3">
      {brand.toUpperCase()}
    </text>
  </>
);

/* fallback: category icon on gradient (only used when no specific match) */
const CategoryFallback = ({ category, brand, accent = "#94A3B8" }) => (
  <>
    <defs>
      <linearGradient id="fbg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1F2937" />
        <stop offset="100%" stopColor="#0F172A" />
      </linearGradient>
    </defs>
    <rect width="320" height="240" fill="url(#fbg)" />
    <g opacity="0.18">
      {Array.from({ length: 16 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="240" stroke="#FFFFFF" strokeWidth="0.5" />
      ))}
    </g>
    <g transform="translate(160, 95)" opacity="0.5">
      {/* Generic box silhouette */}
      <rect x="-44" y="-32" width="88" height="64" rx="4" fill="none" stroke={accent} strokeWidth="2" />
      <path d="M -44 -10 L 44 -10" stroke={accent} strokeWidth="1.2" />
      <path d="M 0 -32 L 0 32" stroke={accent} strokeWidth="1.2" />
    </g>
    <text x="160" y="180" textAnchor="middle" fontSize="11" fontWeight="700" fill={accent} letterSpacing="2.5">
      {(brand || "").toUpperCase()}
    </text>
    <text x="160" y="200" textAnchor="middle" fontSize="9" fontWeight="600" fill="#94A3B8" letterSpacing="2.5" opacity="0.7">
      {(category || "").toUpperCase()}
    </text>
  </>
);

/* ---------------------------- product matcher ---------------------------- */
function pickIllustration(product) {
  const mfg = (product?.mfg_pn || "").toUpperCase();
  const brand = (product?.brand || "").toLowerCase();
  const cat = (product?.category || "").toLowerCase();
  const desc = (product?.description || "").toLowerCase();

  // bearings
  if (mfg.includes("6205") || (cat.includes("power transmission") && desc.includes("bearing")))
    return <Bearing brand={product.brand} accent="#7DD3FC" />;
  // Donaldson air filter
  if (brand === "donaldson" || mfg.startsWith("P18") || (cat.includes("hvac") && desc.includes("filter")))
    return <AirFilter brand={product.brand} accent="#FCD34D" />;
  // Spin-on oil filters (Fleetguard, etc.)
  if (brand === "fleetguard" || (desc.includes("spin-on") || (desc.includes("lube") && desc.includes("filter"))))
    return <SpinOnFilter brand={product.brand} accent="#FBBF24" />;
  // N95
  if (mfg.includes("1860") || desc.includes("n95"))
    return <N95Respirator brand={product.brand} accent="#FB923C" />;
  // Hard hat
  if (brand === "msa" || desc.includes("hard hat"))
    return <HardHat brand={product.brand} accent="#FBBF24" />;
  // Safety goggles / glasses
  if (desc.includes("goggle") || desc.includes("glasses"))
    return <Goggles brand={product.brand} accent="#7DD3FC" />;
  // Drill / impact driver
  if (desc.includes("drill") || desc.includes("impact driver"))
    return <PowerDrill brand={product.brand} accent={brand === "dewalt" ? "#FACC15" : "#3B82F6"} />;
  // Belt sander
  if (desc.includes("belt sander") || desc.includes("sander"))
    return <BeltSander brand={product.brand} accent="#22D3EE" />;
  // LED T8 tube
  if (desc.includes("led") && desc.includes("tube"))
    return <LedTube brand={product.brand} accent="#FCD34D" />;
  // HID / metal halide lamp
  if (desc.includes("hid") || desc.includes("metal halide") || desc.includes("lamp"))
    return <HidLamp brand={product.brand} accent="#FBBF24" />;
  // Loctite / threadlocker
  if (brand === "loctite" || desc.includes("threadlocker"))
    return <Bottle brand={product.brand} accent="#3B82F6" colour="#1E40AF" label={mfg.includes("243") ? "243" : ""} />;
  // VHB / acrylic tape
  if (desc.includes("vhb") || desc.includes("tape"))
    return <TapeRoll brand={product.brand} accent="#FB923C" />;
  // Pumps
  if (cat.includes("pumps") || desc.includes("pump"))
    return <Pump brand={product.brand} accent="#67E8F9" />;
  // Power cable
  if (desc.includes("cable") || mfg.includes("NYY"))
    return <CableCoil brand={product.brand} accent="#FCD34D" />;
  // MCB / circuit breaker
  if (desc.includes("mcb") || desc.includes("circuit breaker") || mfg.includes("S203"))
    return <MCB brand={product.brand} accent="#FB923C" />;
  // Push button
  if (desc.includes("push button") || desc.includes("button switch"))
    return <PushButton brand={product.brand} accent="#22C55E" />;
  // Pneumatic / hydraulic cylinder
  if (desc.includes("pneumatic cylinder") || desc.includes("hydraulic cylinder") || cat.includes("pneumatic") || cat.includes("hydraulic"))
    return <Cylinder brand={product.brand} accent={cat.includes("hydraulic") ? "#67E8F9" : "#7DD3FC"} />;
  // Multimeter
  if (desc.includes("multimeter") || mfg.includes("FLUKE-117"))
    return <Multimeter brand={product.brand} accent="#FACC15" />;
  // Caliper
  if (desc.includes("caliper"))
    return <Caliper brand={product.brand} accent="#FACC15" />;
  // Marker pen
  if (desc.includes("marker pen") || desc.includes("marker"))
    return <MarkerPen brand={product.brand} accent="#FBBF24" />;
  // Welding helmet
  if (desc.includes("welding helmet") || (brand === "lincoln electric"))
    return <WeldingHelmet brand={product.brand.split(" ")[0]} accent="#FCA5A5" />;
  // Welding electrodes
  if (desc.includes("welding electrode") || desc.includes("electrode"))
    return <WeldingElectrodes brand={product.brand} accent="#FCA5A5" />;
  // Oil drum / hydraulic oil
  if (desc.includes("hydraulic oil") || desc.includes("oil") && desc.includes("20l") || desc.includes("208l"))
    return <OilDrum brand={product.brand} accent="#FCD34D" main={brand === "castrol" ? "#16A34A" : "#1E3A8A"} />;
  // Padlock / lockout
  if (desc.includes("padlock") || desc.includes("lockout"))
    return <Padlock brand={product.brand} accent="#FCA5A5" />;
  // Cleaning bottle / concentrate
  if (cat.includes("cleaning") && (desc.includes("cleaner") || desc.includes("concentrate")))
    return <SprayBottle brand={product.brand} accent="#7DD3FC" />;
  // Paper ream
  if (desc.includes("paper") || desc.includes("copier paper") || desc.includes("a4"))
    return <PaperReam brand={product.brand} accent="#7DD3FC" />;
  // Industrial wipers
  if (desc.includes("wipers") || desc.includes("wipes"))
    return <WipersBox brand="WypAll" accent="#3B82F6" />;

  return <CategoryFallback brand={product.brand} category={product.category} />;
}

export default function ProductIllustration({ product, className = "" }) {
  const themeAccent = "#7DD3FC";
  const inner = pickIllustration(product);

  // CategoryFallback returns its own bg, others share Backdrop.
  const usesBackdrop = inner && inner.type !== CategoryFallback;

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid={`product-illustration-${product?.infoshop_pn}`}>
      <svg viewBox="0 0 320 240" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label={product?.description}>
        {usesBackdrop && <Backdrop accent={themeAccent} />}
        {inner}
      </svg>
    </div>
  );
}
